import { ATTENTION_KIND } from '@klopotun/domain';
import { z } from 'zod';
import { amountMinorSchema, civilDateSchema, currencyCodeSchema, uuidSchema } from './common.js';
import { intervalUnitSchema } from './payment.js';
export const attentionKindSchema = z.enum(ATTENTION_KIND);
const ATTENTION_ID_SEPARATOR = ':';
const ATTENTION_SUBJECT_PATTERN = /^[0-9A-Za-z:.-]{1,200}$/;
const isAttentionId = (value) => {
    const separatorIndex = value.indexOf(ATTENTION_ID_SEPARATOR);
    const hasKind = separatorIndex > 0;
    const isKnownKind = hasKind && attentionKindSchema.safeParse(value.slice(0, separatorIndex)).success;
    return isKnownKind && ATTENTION_SUBJECT_PATTERN.test(value.slice(separatorIndex + 1));
};
export const attentionIdSchema = z.string().refine(isAttentionId, 'not an attention item');
const attentionAmountSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('fixed'),
        amountMinor: amountMinorSchema,
        currency: currencyCodeSchema,
    }),
    z.object({
        kind: z.literal('range'),
        minMinor: amountMinorSchema,
        maxMinor: amountMinorSchema,
        currency: currencyCodeSchema,
    }),
]);
const dayCountSchema = z.int().min(0);
const chargeSoonItemSchema = z.object({
    id: attentionIdSchema,
    kind: z.literal(ATTENTION_KIND.CHARGE_SOON),
    dueDate: civilDateSchema,
    paymentId: uuidSchema,
    occurrenceId: uuidSchema,
    name: z.string().min(1),
    daysLeft: dayCountSchema,
    amount: attentionAmountSchema.nullable(),
    isLowBalance: z.boolean(),
});
const yearlySoonItemSchema = z.object({
    id: attentionIdSchema,
    kind: z.literal(ATTENTION_KIND.YEARLY_SOON),
    dueDate: civilDateSchema,
    paymentId: uuidSchema,
    occurrenceId: uuidSchema,
    name: z.string().min(1),
    daysLeft: dayCountSchema,
    amount: attentionAmountSchema.nullable(),
    cancelUrl: z.string().min(1).nullable(),
    isLowBalance: z.boolean(),
});
const payYourselfItemSchema = z.object({
    id: attentionIdSchema,
    kind: z.literal(ATTENTION_KIND.PAY_YOURSELF),
    dueDate: civilDateSchema,
    paymentId: uuidSchema,
    occurrenceId: uuidSchema,
    name: z.string().min(1),
    daysOverdue: dayCountSchema,
    unpaidCount: z.int().min(1),
    amount: attentionAmountSchema.nullable(),
});
const missedChargeItemSchema = z.object({
    id: attentionIdSchema,
    kind: z.literal(ATTENTION_KIND.MISSED_CHARGE),
    dueDate: civilDateSchema,
    paymentId: uuidSchema,
    occurrenceId: uuidSchema,
    name: z.string().min(1),
    amount: attentionAmountSchema.nullable(),
    isStopped: z.boolean(),
});
const newSubscriptionItemSchema = z.object({
    id: attentionIdSchema,
    kind: z.literal(ATTENTION_KIND.NEW_SUBSCRIPTION),
    dueDate: civilDateSchema.nullable(),
    paymentId: uuidSchema,
    name: z.string().min(1),
    amount: attentionAmountSchema.nullable(),
    intervalUnit: intervalUnitSchema,
});
const priceChangedItemSchema = z.object({
    id: attentionIdSchema,
    kind: z.literal(ATTENTION_KIND.PRICE_CHANGED),
    dueDate: z.null(),
    paymentId: uuidSchema,
    name: z.string().min(1),
    previousAmountMinor: amountMinorSchema,
    amountMinor: amountMinorSchema,
    currency: currencyCodeSchema,
    changedOn: civilDateSchema,
});
export const attentionItemSchema = z.discriminatedUnion('kind', [
    chargeSoonItemSchema,
    yearlySoonItemSchema,
    payYourselfItemSchema,
    missedChargeItemSchema,
    newSubscriptionItemSchema,
    priceChangedItemSchema,
]);
export const attentionListSchema = z.object({
    today: civilDateSchema,
    items: z.array(attentionItemSchema),
});
//# sourceMappingURL=attention.js.map